import { LockKeyhole } from "lucide-react";
import { lazy, Suspense, type ComponentType } from "react";
import { useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { EmptyState } from "../components/ui";
import { useAuth } from "../lib/auth-context";
import { modules } from "../lib/modules";
import { ModulePlaceholderPage } from "./module-placeholder";
import styles from "./module-page.module.css";

const MaiaPage = lazy(() =>
  import("../features/maia").then((module) => ({ default: module.MaiaPage })),
);
const TicketSearchPage = lazy(() =>
  import("../features/maia-ticket-search/ticket-search").then((module) => ({
    default: module.TicketSearchPage,
  })),
);
const WidgetsPage = lazy(() =>
  import("../features/widgets").then((module) => ({
    default: module.WidgetsPage,
  })),
);
const LlmGatewayPage = lazy(() =>
  import("../features/llm-gateway").then((module) => ({
    default: module.LlmGatewayPage,
  })),
);
const KlientiksTelemedPage = lazy(() =>
  import("../features/klientiks-telemed").then((module) => ({
    default: module.KlientiksTelemedPage,
  })),
);
const ClinicalRecommendationsPage = lazy(() =>
  import("../features/clinical-recommendations").then((module) => ({
    default: module.ClinicalRecommendationsPage,
  })),
);
const DocxConverterPage = lazy(() =>
  import("../features/document-tools").then((module) => ({
    default: module.DocxConverterPage,
  })),
);
const HtmlHandlerPage = lazy(() =>
  import("../features/document-tools").then((module) => ({
    default: module.HtmlHandlerPage,
  })),
);
const MediaLibraryPage = lazy(() =>
  import("../features/media-library").then((module) => ({
    default: module.MediaLibraryPage,
  })),
);
const ValidatorSemdPage = lazy(() =>
  import("../features/validator-semd").then((module) => ({
    default: module.ValidatorSemdPage,
  })),
);
const TranscriptionAnalysisPage = lazy(() =>
  import("../features/transcription-analysis").then((module) => ({
    default: module.TranscriptionAnalysisPage,
  })),
);
const MaiaImportsPage = lazy(() =>
  import("../features/maia-imports").then((module) => ({
    default: module.MaiaImportsPage,
  })),
);

function CmdLabPage() {
  useEffect(() => {
    window.location.replace("/modules/cmdlab");
  }, []);
  return <div aria-live="polite">Загрузка CMD Lab...</div>;
}

const modulePages: Record<string, ComponentType> = {
  "maia-ticket-search": TicketSearchPage,
  widgets: WidgetsPage,
  maia: MaiaPage,
  "llm-gateway": LlmGatewayPage,
  "klientiks-telemed": KlientiksTelemedPage,
  "clinical-recommendations": ClinicalRecommendationsPage,
  "docx-converter": DocxConverterPage,
  "html-handler": HtmlHandlerPage,
  "media-library": MediaLibraryPage,
  "validator-semd": ValidatorSemdPage,
  "transcription-analysis": TranscriptionAnalysisPage,
  "maia-imports": MaiaImportsPage,
  cmdlab: CmdLabPage,
};

export function ModulePage() {
  const { moduleId } = useParams({ from: "/_protected/modules/$moduleId" });
  const { can } = useAuth();
  const knownModule = modules.find((item) => item.id === moduleId);
  if (knownModule && !can(moduleId)) {
    return (
      <EmptyState
        icon={<LockKeyhole size={28} />}
        title="Нет доступа к модулю"
        description={`Для работы с разделом «${knownModule.title}» обратитесь к администратору.`}
      />
    );
  }
  const Component = modulePages[moduleId] || ModulePlaceholderPage;
  return (
    <div
      className={`${styles.viewport} ${
        moduleId === "maia-ticket-search"
          ? styles.ticketSearch
          : moduleId === "widgets" ||
              moduleId === "maia" ||
              moduleId === "transcription-analysis" ||
              moduleId === "maia-imports"
            ? styles.scrollable
            : styles.document
      }`}
    >
      <Suspense fallback={<div aria-busy="true">Загрузка модуля...</div>}>
        <Component />
      </Suspense>
    </div>
  );
}
